import React, { useRef, useEffect, useState, Suspense, useCallback, useMemo } from "react";
import { TransformControls, Plane, PivotControls, useTexture} from "@react-three/drei";
import { useXR, useXREvent } from "@react-three/xr";
import { useObjectStore } from "../zustand/objects";
import { useThree } from "@react-three/fiber";
import * as THREE from "three";

import debounce from 'lodash.debounce';

 
import {CurvedPanelsTexture} from "./ModelFiles/UI/CurvedPanels" 
import { PreviewDestinationOutlines } from "./PreviewDestinationOutlines";
import VRControls from "./VRControls";
import {getFigmaFrameFromID} from "../api";
import { matrix4FromState } from "../utils/matrix4FromState";

export function FigmaObject({ id, currentObjectArtboard, objectsAreLoaded, colour, figmaURL, figmaSize, isLocked, matrixState, curved, curveAmount }) {

// WE NEED A FUNCTION IN THE STORE THAT GETS THE LATEST URL FOR A FRAME AND HOLDS IT, 
// SO THAT ALL PEOPLE A SYNCED TO THIS URL AND SO THAT IT CAN BE UPDATED FROM ANY COMPONENT

  const {
    updateObjectSelected,
    selectedObjectID,
    transformMode,
    updateObjectMatrix,
    transformModeChange,
    setIsDraggingModel,
    isDraggingModel,
    projectKey,
    arrangeUndoRedo
  } = useObjectStore();

  const transformControls = useRef();
  const transformChild = useRef();
  const grabcontrols = useRef();

  async function updatePositionVR() {
     // send current state to undoActions
     arrangeUndoRedo({
      updateType: "objectUpdate", 
      artboard: currentObjectArtboard, 
      objectID: id,
      undo:false, 
      projectKey: projectKey, 
      field: "matrixState",
      })
      // send update to zustand
    updateObjectMatrix({
      id,
      currentObjectArtboard,
      matrix: matrix4FromState(grabcontrols.current?.matrixWorld),
      undo: false
    })
  }


const [vrSelected, setVrSelected] = useState(false)

const xr = useXR()

const handleVRSelectEnd = () => {
  if (xr.isPresenting) {
       if (vrSelected){
       updatePositionVR().then(() => grabcontrols.current.position.set(
         0,0,0
       )).then(() => grabcontrols.current.rotation.set(
        0,0,0
      )).then(() => setVrSelected(false))
}
    }
}

useXREvent('selectend', () => handleVRSelectEnd())


  // handleDrag removed: it was unused and caused lint warnings

 
  // declare empty position, rotation and scale for .decompose()
  const pos = new THREE.Vector3()
  const rot = new THREE.Quaternion()
  const sca = new THREE.Vector3()

  const newMatrix = matrix4FromState(matrixState);

  // decompose newMatrix and apply to pos, rot and sca
  newMatrix.decompose(pos, rot, sca)
  // create matrix for gizmo that doesn't include scale, which is applied to the group instead
  // the below stops the object snapping back to old position while moving


  const [interimMatrix, setInterimMatrix] = useState(() => matrix4FromState(matrixState))

  const startDragging = () => {
    setIsDraggingModel(true)
  }

  const stopDragging = () => {
    setIsDraggingModel(false)
  }

  const handleDragging = useMemo(
    () =>
      debounce((e) => {
        if (selectedObjectID === id) {
          setInterimMatrix(e);
        }
      }, 30),
    [id, selectedObjectID]
  );

  useEffect(() => {
    return () => handleDragging.cancel();
  }, [handleDragging]);

  const ObjectUpdate = (e) => {
    
    const pos2 = new THREE.Vector3()
    const rot2 = new THREE.Quaternion()
    const sca2 = new THREE.Vector3() 
    const toDecompose = matrix4FromState(e)
    toDecompose.decompose(pos2, rot2, sca2)
     // send current state to undoActions
     arrangeUndoRedo({
      updateType: "objectUpdate", 
      artboard: currentObjectArtboard, 
      objectID: id,
      undo:false, 
      projectKey: projectKey, 
      field: "matrixState",
      })
      // send update to zustand
    updateObjectMatrix({
      id,
      currentObjectArtboard,
      matrix: e,
      undo: false
    })}


  const missedShape = () => {
    !isLocked && !isDraggingModel && updateObjectSelected('0')
    transformModeChange({
      transformMode: "translate"
    })
  };

  

 
  const passdownFunctionEditor = () => {
    // !isDraggingModel && !isLocked ? !grouping ? updateObjectSelected(id) : addToGroupedObjects(id) : updateObjectSelected('0') && transformModeChange({
    //   transformMode: "translate"
    // })
    if (isDraggingModel === false) {
      !isLocked ? updateObjectSelected(id) : updateObjectSelected('0')  
    }
  }



  if (!objectsAreLoaded) {
    return
  } else {
    return (
      selectedObjectID === id ? !isLocked ? transformMode === "translate" ?
        <group onPointerMissed={(e) => {
          // e.stopPropagation()
          missedShape()
        }}>
          <PivotControls
            depthTest={false}
            lineWidth={5}
            matrix={!isDraggingModel && matrix4FromState(matrixState)}
            active={selectedObjectID === id ? [true, true, true] : [false, false, false]}

            onDragStart={() => startDragging()}
            onDragEnd={() => {
              ObjectUpdate(interimMatrix)
              stopDragging()}}
            onDrag={(e) => handleDragging(e)}

            ref={transformControls}
            visible={selectedObjectID === id ? true : false}
            fixed={true}
             scale={150}
            annotations={false} 
          >
            <group ref={transformChild}
            >
              <Suspense fallback={null}>
              {figmaURL && !curved ? <FigmaImagePlane figmaURL={figmaURL} figmaSize={figmaSize} />
              : <CurvedPanelsTexture colour={colour} clickFunction={passdownFunctionEditor} imageTexture={figmaURL} curveAmount={curveAmount} width={figmaSize.x / 800} height={figmaSize.y / 400}/>
              }
                {/* <Image transparent opacity={1} ref={imageRef} position={[0, imageSize.height / 1600, 0]} scale={[imageSize.width / 800, imageSize.height / 800, 1]} url={image} /> */}
              </Suspense></group>
          </PivotControls></group> :
        <TransformControls
          position={objectsAreLoaded && new THREE.Vector3().setFromMatrixPosition(newMatrix)}
          // rotation={objectsAreLoaded && new THREE.Euler().setFromRotationMatrix(matrixState)}
          rotation={objectsAreLoaded && new THREE.Euler().setFromQuaternion(rot)}
          scale={objectsAreLoaded && new THREE.Vector3().setFromMatrixScale(newMatrix)}
          showY={selectedObjectID === id ? true : false}
          showX={selectedObjectID === id ? true : false}
          showZ={selectedObjectID === id ? true : false}
          // showX={selectedObjectID === id && (transformMode === "translate" || transformMode === "scale") ? true : (category === "shape") ? selectedObjectID === id && true : false}
          // showZ={selectedObjectID === id && (transformMode === "translate" || transformMode === "scale") ? true : false}
          translationSnap={0.125}
          // rotationSnap={0.31416}
          scaleSnap={0.025}
          ref={transformControls}
          mode={'scale'}
        >
          <group ref={transformChild} onClick={() => passdownFunctionEditor()}>
            <Suspense fallback={null}>
            {figmaURL && !curved ? <FigmaImagePlane figmaURL={figmaURL} figmaSize={figmaSize} />
              : <CurvedPanelsTexture colour={colour} clickFunction={passdownFunctionEditor} imageTexture={figmaURL} curveAmount={curveAmount} width={figmaSize.x / 800} height={figmaSize.y / 400}/>
              }
            </Suspense>
          </group>
        </TransformControls>

        :
        <TransformControls
          position={objectsAreLoaded && new THREE.Vector3().setFromMatrixPosition(newMatrix)}
          rotation={objectsAreLoaded && new THREE.Euler().setFromRotationMatrix(newMatrix)}
          scale={objectsAreLoaded && new THREE.Vector3().setFromMatrixScale(newMatrix)}
          showY={false}
          showX={false}
          showZ={false}
          translationSnap={0.125}
          // rotationSnap={0.31416}
          scaleSnap={0.025}
          ref={transformControls}
          mode={transformMode}
        >
          <Suspense fallback={null}>
            <group onClick={() => passdownFunctionEditor()}>
            {figmaURL && !curved ? <FigmaImagePlane figmaURL={figmaURL} figmaSize={figmaSize} />
              : <CurvedPanelsTexture colour={colour} clickFunction={passdownFunctionEditor} imageTexture={figmaURL} curveAmount={curveAmount} width={figmaSize.x / 800} height={figmaSize.y / 400}/>
              }
            </group>
          </Suspense>
        </TransformControls>
        :
        <VRControls
          isLocked={isLocked}
          idprop={id}
          currentObjectArtboard={currentObjectArtboard}
          objectsAreLoaded={objectsAreLoaded}
          matrixState={matrix4FromState(matrixState)}
          rot={rot}
          scale={sca}
        >
          <Suspense fallback={null}>
            <group onClick={() => passdownFunctionEditor()}>
                  {figmaURL && !curved ? <FigmaImagePlane figmaURL={figmaURL} figmaSize={figmaSize} />
              : <CurvedPanelsTexture colour={colour} clickFunction={passdownFunctionEditor} imageTexture={figmaURL} curveAmount={curveAmount} width={figmaSize.x / 800} height={figmaSize.y / 400}/>
              }
            </group>
          </Suspense>
        </VRControls>
    );
  }
};


export function GroupedFigmaObject({ id, objectsAreLoaded, colour, figmaID, matrixState, curved, curveAmount }) {

  const { scene } = useThree();
  const modelRef = useRef()

  const [figmaURL, setFigmaURL] = useState()
  const [figmaSize, setfigmaSize] = useState()

  const configureFigmaFrame = useCallback((url, size) => {
    setFigmaURL(url)
    setfigmaSize(size)
  }, [])

  useEffect(() => {
    Promise.resolve(scene.getObjectByName(id).remove(modelRef.current))
      .then(Promise.resolve(modelRef.current.applyMatrix4(matrix4FromState(matrixState))))
      .then(Promise.resolve(scene.getObjectByName(id).attach(modelRef.current)))
    getFigmaFrameFromID(figmaID).then((e) => configureFigmaFrame(e.URL, e.size))
  }, [configureFigmaFrame, figmaID, id, matrixState, scene]);

  const texture = useTexture(figmaURL)

  const imageRef = useRef()
  if (!objectsAreLoaded) {
    return
  } else {
    return (
      <group
        ref={modelRef}
      // scale={scale}
      >
        <Suspense fallback={null}>
          <group>
          {figmaURL && !curved ? <Plane ref={imageRef} position={[0,figmaSize.y * 2, 0]} smoothness={4} scale={figmaSize && [figmaSize.x / 400, figmaSize.y / 400, 1]}>
                      <meshPhongMaterial
                        receiveShadow
                        attach="material"
                        opacity={1} transparent
                        map={figmaURL && texture}
                      />
                    </Plane>
              : <CurvedPanelsTexture colour={colour} imageTexture={figmaURL} curveAmount={curveAmount} width={figmaSize.x / 800} height={figmaSize.y / 400}/>
              }
          
          </group>
        </Suspense>
      </group>
    );
  }
};


//PREVIEW VERSION
export const PreviewFigmaObject = ({ destination, colour, pose, figmaID, figmaURL, figmaSize, matrixState, curved, curveAmount }) => {

  const { shapeHoverChange, updateArtboardAndPreviewCamera } = useObjectStore();
  const [destPointerOver, setDestPointerOver] = useState(false);

  const previewMatrix = matrix4FromState(matrixState);

  const passdownFunctionPreview = () => {
    destination && updateArtboardAndPreviewCamera(destination)
    destination && shapeHoverChange(false);
    setDestPointerOver(false);

  }

  const hoverShape = (e) => {
    shapeHoverChange(e)
  }

  const onDestinationPointerOver = () => {
    if (!destination) return;
    hoverShape(true);
    setDestPointerOver(true);
  };
  const onDestinationPointerOut = () => {
    if (!destination) return;
    hoverShape(false);
    setDestPointerOver(false);
  };
  const showDestinationOutline = Boolean(destination) && destPointerOver;


  return ( 
    <>
      <Suspense fallback={null}>
        <group
          // scale={scale}
          scale={new THREE.Vector3().setFromMatrixScale(previewMatrix)}
          position={new THREE.Vector3().setFromMatrixPosition(previewMatrix)}
          rotation={new THREE.Euler().setFromRotationMatrix(previewMatrix)}
          onPointerOver={onDestinationPointerOver}
          onPointerOut={onDestinationPointerOut} destination={destination} onClick={passdownFunctionPreview} colour={colour} pose={pose}>
            {figmaURL && !curved ? <FigmaImagePlane showDestinationOutline={showDestinationOutline} figmaID={figmaID} figmaURL={figmaURL} figmaSize={figmaSize} />
              : <CurvedPanelsTexture showDestinationOutline={showDestinationOutline} figmaID={figmaID} figmaURL={figmaURL} colour={colour} imageTexture={figmaURL} curveAmount={curveAmount} width={figmaSize.x / 800} height={figmaSize.y / 400}/>
              }
        </group>
      </Suspense>

    </>
  ); 
};


// THE PLANE IN A NESTED OBJECT TO ALLOW USETEXTURE TO WORK

const FigmaImagePlane = ({ figmaURL, figmaSize, showDestinationOutline }) => {

//   var request = new XMLHttpRequest();  
// request.open('GET', figmaURL, true);
// request.onreadystatechange = function(){
   
//     if (request.readyState === 4){
//         if (request.status === 404) {  
//             console.log("404")
//         }  
//     }
//     else if (request.status === 200){
//       setReady(true)
//     }
// };

// useEffect(() => {
  
// request.send();
// },[])

 

  const imageRef = useRef()
 
  
  const texture = useTexture(figmaURL)


 return (
  <Suspense fallback={null}>
  {texture && <Plane  ref={imageRef} position={[0, figmaSize.y / 800, 0]} smoothness={4} scale={[figmaSize.x / 400, figmaSize.y / 400, 1]}>
                     <meshPhongMaterial
                        receiveShadow
                        attach="material"
                        opacity={1} transparent
                        map={texture}
                        side={THREE.DoubleSide}
                      />
                      {showDestinationOutline && <PreviewDestinationOutlines />}
                    </Plane>
                    }
                    </Suspense>)
}


